import type { RuntimeBus } from "@/runtime/bus";
import type { TtsAdapter } from "@/adapters/contracts";

export interface TtsSpeakOptions {
  abortSignal?: AbortSignal;
}

export interface TtsAdapterContext {
  bus: RuntimeBus;
}

/**
 * Base class for every TTS adapter. Adapters always run on the main thread.
 * If an adapter needs heavy inference off the main thread (e.g. ONNX / wasm),
 * it delegates that internally — callers of the adapter do not see where the
 * compute happens. Cloud adapters will extend this same base class and reach
 * the network from inside speak().
 *
 * Every adapter publishes to the same RuntimeBus events the rest of the app
 * subscribes to (`tts.audio`, `tts.done`, `error`) so local vs cloud vs
 * future-remote can be hot-swapped per thread via engine profile.
 */
export abstract class BaseTtsAdapter implements TtsAdapter {
  protected readonly bus: RuntimeBus;

  constructor(context: TtsAdapterContext) {
    this.bus = context.bus;
  }

  abstract speak(text: string, options?: TtsSpeakOptions): Promise<void>;

  // Default no-op implementations. Adapters that maintain long-lived state
  // (websocket connections, native playback handles, etc.) override these.
  async interrupt(): Promise<void> {
    // Adapters that own their playback resources should stop them here.
  }

  async stop(): Promise<void> {
    await this.interrupt();
  }

  protected publishAudio(pcm: Float32Array, sampleRate: number): void {
    this.bus.publish({ type: "tts.audio", pcm, sampleRate });
  }

  protected publishDone(): void {
    this.bus.publish({ type: "tts.done" });
  }

  protected publishError(message: string): void {
    this.bus.publish({ type: "error", domain: "tts", message });
  }
}
